import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Flag, PartyPopper, ChevronRight } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";

interface SetNationPromptModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSetNation: () => void;
}

export default function SetNationPromptModal({ isOpen, onClose, onSetNation }: SetNationPromptModalProps) {
  const isMobile = useIsMobile();

  const handleSetNation = () => {
    onClose();
    onSetNation();
  };

  const Content = () => (
    <div className="flex flex-col items-center text-center py-4">
      <div className="relative mb-6">
        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary/20 to-green-500/20 flex items-center justify-center">
          <PartyPopper className="w-10 h-10 text-primary" />
        </div>
      </div>

      <h3 className="font-display text-xl font-bold text-foreground mb-2">
        Welcome to Premium! 🎉
      </h3>
      <p className="text-muted-foreground mb-6 max-w-sm">
        Your subscription is now active. Set your favorite nation to personalize 
        your dashboard with games, players, and alerts for your country.
      </p>

      <div className="w-full space-y-3">
        <Button 
          onClick={handleSetNation} 
          className="w-full gap-2"
        >
          <Flag className="w-4 h-4" />
          Set My Favorite Nation
          <ChevronRight className="w-4 h-4" />
        </Button>
        <Button 
          onClick={onClose} 
          variant="ghost" 
          className="w-full text-muted-foreground"
        >
          I'll do this later
        </Button>
      </div>

      <div className="mt-6 flex items-center gap-3 text-sm text-muted-foreground">
        <div className="flex gap-1">
          <span className="text-lg">🇨🇦</span>
          <span className="text-lg">🇸🇪</span>
          <span className="text-lg">🇫🇮</span>
          <span className="text-lg">🇺🇸</span>
        </div>
        <span>Support your national team</span>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <Drawer open={isOpen} onOpenChange={onClose}>
        <DrawerContent className="px-4 pb-6">
          <DrawerHeader className="text-center">
            <DrawerTitle className="sr-only">Set Your Favorite Nation</DrawerTitle>
            <DrawerDescription className="sr-only">
              Personalize your dashboard with your favorite national team
            </DrawerDescription>
          </DrawerHeader>
          <Content />
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md bg-card border-border">
        <DialogHeader>
          <DialogTitle className="sr-only">Set Your Favorite Nation</DialogTitle>
          <DialogDescription className="sr-only">
            Personalize your dashboard with your favorite national team
          </DialogDescription>
        </DialogHeader>
        <Content />
      </DialogContent>
    </Dialog>
  );
}
